import { apiError } from "../../../../../utils/formErrors";
import React, { useCallback, useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import Topbar from "../../../../../component/TopBar";
import EmptyState from "../../../../../component/EmptyState";
import ChecklistView from "../../../../../component/onboarding/ChecklistView";
import { useNotify } from "../../../../../component/NotificationProvider";
import { getMyChecklist, updateChecklistItem } from "../../../../../services/onboarding";
import { tokens, radius } from "../../../../../theme";

/**
 * Your own onboarding.
 *
 * Deliberately shows the organisation's items as well as yours. A new joiner
 * asking "has anyone ordered my laptop?" is the normal case, and a list that
 * hid everything except their own homework would answer it with silence.
 */
const MyOnboarding = () => {
  const notify = useNotify();
  const [checklist, setChecklist] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyItemId, setBusyItemId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMyChecklist();
      setChecklist(res?.data || null);
      setProgress(res?.data?.progress || null);
    } catch (err) {
      notify.error(err?.message || "Couldn't load your onboarding");
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  const onUpdate = async (item, input) => {
    setBusyItemId(item._id);
    try {
      const res = await updateChecklistItem(checklist._id, item._id, input);
      if (res?.success === false) throw apiError(res);
      setChecklist(res?.data?.checklist || checklist);
      setProgress(res?.data?.progress || progress);
      // a document waits for HR rather than counting as done
      notify.success(res?.data?.submitted ? "Submitted — HR will verify it" : "Updated");
    } catch (err) {
      notify.error(err?.message || "Couldn't update that item");
      load();
    } finally {
      setBusyItemId(null);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Topbar MainHeading="My onboarding" />
      <Box sx={{ padding: "0 1.5rem 2rem" }}>
        <Box
          sx={{
            background: tokens.card,
            border: `1px solid ${tokens.border}`,
            borderRadius: `${radius.card}px`,
            padding: "1.25rem",
          }}
        >
          {loading ? (
            <Box sx={{ textAlign: "center", padding: "2rem" }}>
              <CircularProgress size={24} sx={{ color: tokens.textMuted }} />
            </Box>
          ) : !checklist ? (
            <EmptyState
              title="No onboarding checklist"
              description="Your organisation hasn't set one up, or you joined before it existed."
            />
          ) : (
            <ChecklistView
              checklist={checklist}
              progress={progress}
              viewerRole="self"
              onUpdate={onUpdate}
              busyItemId={busyItemId}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default MyOnboarding;
